import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { chatService } from "@b2b/services";
import type { ChatConversationListItem } from "@b2b/services/api/modules/chat/chat.types";
import type { HeaderChatUnread } from "@b2b/ui-components";
import { useAuth } from "../auth/Authprovider";
import { B2B_CHAT_UNREAD_REFRESH_EVENT } from "./chatUnreadEvents";

type Ctx =
  | { mode: "active"; data: HeaderChatUnread }
  | { mode: "inactive" };

const ChatUnreadHeaderContext = createContext<Ctx | undefined>(undefined);

function formatCounterpartName(c: ChatConversationListItem): string {
  const p = c.counterpart;
  const n = `${p?.firstName || ""} ${p?.lastName || ""}`.trim();
  return n || p?.email || "Contact";
}

function previewForConversation(c: ChatConversationListItem): string {
  const lm = c.lastMessage;
  if (!lm) return "New conversation";
  const text = String(lm.message ?? "").trim();
  if (lm.attachments?.length && (!text || text === "[attachment]")) return "Photo";
  if (!text) return "Message";
  return text.length > 72 ? `${text.slice(0, 72)}…` : text;
}

function mapToThreads(items: ChatConversationListItem[]): HeaderChatUnread["unreadThreads"] {
  return items.map((c) => ({
    conversationId: c.id,
    title: formatCounterpartName(c),
    preview: previewForConversation(c),
    unreadCount: Number(c.unreadCount || 0),
    avatarUrl: c.counterpart?.profileImage,
  }));
}

export function ChatUnreadHeaderProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, userRole } = useAuth();
  const navigate = useNavigate();

  const eligible =
    isAuthenticated && !isLoading && userRole !== undefined && userRole !== "admin";

  const [threads, setThreads] = useState<HeaderChatUnread["unreadThreads"]>([]);
  const [totalUnread, setTotalUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const threadsRef = useRef(threads);
  threadsRef.current = threads;

  const load = useCallback(async () => {
    if (!eligible) return;
    setLoading(true);
    try {
      const data = await chatService.listConversations({ page: 1, limit: 50 });
      const items = (data.items || []) as ChatConversationListItem[];
      const unreadItems = items.filter((c) => Number(c.unreadCount || 0) > 0);
      const total = unreadItems.reduce((s, c) => s + Number(c.unreadCount || 0), 0);
      const mapped = mapToThreads(unreadItems);
      setThreads(mapped);
      setTotalUnread(total);
    } catch {
      setThreads([]);
      setTotalUnread(0);
    } finally {
      setLoading(false);
    }
  }, [eligible]);

  useEffect(() => {
    if (!eligible) {
      setThreads([]);
      setTotalUnread(0);
      setLoading(false);
      return;
    }
    void load();
    const interval = window.setInterval(() => void load(), 60_000);
    const onVisible = () => {
      if (document.visibilityState === "visible") void load();
    };
    const onRefresh = () => void load();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener(B2B_CHAT_UNREAD_REFRESH_EVENT, onRefresh);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener(B2B_CHAT_UNREAD_REFRESH_EVENT, onRefresh);
    };
  }, [eligible, load]);

  const onOpenThread = useCallback(
    async (conversationId: string) => {
      const t = threadsRef.current.find((x) => x.conversationId === conversationId);
      const dec = t?.unreadCount ?? 0;
      setThreads((prev) => prev.filter((x) => x.conversationId !== conversationId));
      setTotalUnread((n) => Math.max(0, n - dec));
      await chatService.markRead(conversationId).catch(() => {});
      navigate(`/chat?conversationId=${encodeURIComponent(conversationId)}`);
    },
    [navigate]
  );

  const onOpenAll = useCallback(() => {
    navigate("/chat");
  }, [navigate]);

  const value = useMemo((): Ctx => {
    if (!eligible) return { mode: "inactive" };
    return {
      mode: "active",
      data: {
        totalUnread,
        unreadThreads: threads,
        loading,
        refresh: load,
        onOpenThread,
        onOpenAll,
      },
    };
  }, [eligible, totalUnread, threads, loading, load, onOpenThread, onOpenAll]);

  return (
    <ChatUnreadHeaderContext.Provider value={value}>{children}</ChatUnreadHeaderContext.Provider>
  );
}

export function useChatUnreadHeader(): HeaderChatUnread | undefined {
  const v = useContext(ChatUnreadHeaderContext);
  if (!v || v.mode !== "active") return undefined;
  return v.data;
}
